<template>
  <div class="posts-view">
    <el-row :gutter="16">
      <!-- Боковые фильтры -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5" class="filters-col">
        <SidebarFilters />
      </el-col>

      <!-- Основной контент -->
      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <!-- Панель массовых действий -->
        <transition name="slide-down">
          <div v-if="authStore.canValidatePosts && postsStore.selectedIds.size > 0" class="bulk-actions">
            <span class="bulk-count">Выбрано {{ postsStore.selectedIds.size }} постов</span>
            <el-button-group>
              <el-button type="success" size="small" aria-keyshortcuts="1" @click="bulkValidate('COMPLAINT')">
                <el-icon><Check /></el-icon> Жалоба <kbd class="hotkey">1</kbd>
              </el-button>
              <el-button type="danger" size="small" aria-keyshortcuts="2" @click="bulkValidate('FALSE_POS')">
                <el-icon><Close /></el-icon> Ложное сраб. <kbd class="hotkey">2</kbd>
              </el-button>
            </el-button-group>
            <el-button size="small" @click="postsStore.clearSelection()">Сбросить</el-button>
          </div>
        </transition>

        <!-- Таблица постов -->
        <PostsTable @open-detail="openDetail" />

        <!-- Пагинация -->
        <div class="pagination">
          <el-button
            :disabled="postsStore.page === 0"
            @click="postsStore.prevPage()"
          >
            <el-icon><ArrowLeft /></el-icon> Назад
          </el-button>
          <span class="page-info">Страница {{ postsStore.page + 1 }}</span>
          <el-button
            :disabled="!postsStore.hasMore"
            @click="postsStore.nextPage()"
          >
            Вперёд <el-icon><ArrowRight /></el-icon>
          </el-button>
        </div>
      </el-col>
    </el-row>

    <!-- Детальная карточка поста -->
    <PostDrawer
      v-model="drawerVisible"
      :post-id="selectedPostId"
      @validated="onValidated"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SidebarFilters from '@/components/SidebarFilters.vue'
import PostsTable from '@/components/PostsTable.vue'
import PostDrawer from '@/components/PostDrawer.vue'
import { usePostsStore } from '@/stores/posts'
import { useAuthStore } from '@/stores/auth'
import { validationVerdictFromHotkey, type ValidationVerdict } from '@/components/post-hotkeys'

const postsStore = usePostsStore()
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const drawerVisible = ref(false)
const selectedPostId = ref<number | null>(null)
const bulkValidating = ref(false)
let viewActive = false

onMounted(async () => {
  viewActive = true
  postsStore.page = 0
  await postsStore.loadPosts(true)
  if (!viewActive) return
  openHighlightedPost()
  postsStore.startAutoRefresh()
  window.addEventListener('keydown', handleValidationHotkey)
})

onUnmounted(() => {
  viewActive = false
  postsStore.stopAutoRefresh()
  window.removeEventListener('keydown', handleValidationHotkey)
})

function openDetail(postId: number) {
  selectedPostId.value = postId
  drawerVisible.value = true
}

function onValidated(result: { viaHotkey: boolean; nextPostId: number | null }) {
  if (result.viaHotkey && result.nextPostId !== null) {
    selectedPostId.value = result.nextPostId
    return
  }

  drawerVisible.value = false
  postsStore.page = 0
  void postsStore.loadPosts(true)
}

async function bulkValidate(verdict: ValidationVerdict) {
  if (bulkValidating.value) return

  bulkValidating.value = true
  try {
    await postsStore.validateBulk(verdict)
  } finally {
    bulkValidating.value = false
  }
}

function handleValidationHotkey(event: KeyboardEvent) {
  if (
    drawerVisible.value
    || !authStore.canValidatePosts
    || postsStore.selectedIds.size === 0
    || bulkValidating.value
  ) return

  const verdict = validationVerdictFromHotkey(event)
  if (!verdict) return

  event.preventDefault()
  void bulkValidate(verdict)
}

function openHighlightedPost() {
  const raw = Array.isArray(route.query.highlight)
    ? route.query.highlight[0]
    : route.query.highlight
  const postId = Number(raw)

  if (!Number.isInteger(postId) || postId <= 0) return

  openDetail(postId)
  const { highlight: _highlight, ...query } = route.query
  router.replace({ path: route.path, query })
}
</script>

<style scoped>
.posts-view {
  max-width: 1600px;
  margin: 0 auto;
}

.filters-col {
  margin-bottom: 16px;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  background: var(--app-info-soft);
  border: 1px solid var(--app-info-border);
  border-radius: 8px;
  margin-bottom: 12px;
}

.bulk-count {
  font-weight: 700;
  color: var(--app-text-primary);
  font-size: 15px;
}

.hotkey {
  margin-left: 4px;
  padding: 0 5px;
  border: 1px solid currentColor;
  border-radius: 4px;
  font: inherit;
  font-size: 11px;
  line-height: 17px;
  opacity: 0.8;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 12px 0;
}

.page-info {
  font-size: 15px;
  color: var(--app-text-regular);
  font-weight: 700;
  min-width: 100px;
  text-align: center;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
